import { z } from 'zod';
/**
 * Channel roles and permissions (docs/plans/2026-11-03-channel-roles-design.md). Every channel has
 * its own roles; a person's permissions in a channel are the union of their roles there (or the
 * channel's default role, in a public channel). Novadan only grants signing in and creating channels.
 */
export const ChannelPermission = {
    Read: 'read',
    Write: 'write',
    Files: 'files',
    React: 'react',
    Pin: 'pin',
    MentionEveryone: 'mention_everyone',
    DeleteOthers: 'delete_others',
    VoiceJoin: 'voice_join',
    VoiceSpeak: 'voice_speak',
    Camera: 'camera',
    ScreenShare: 'screen_share',
    VoiceModerate: 'voice_moderate',
    AddMembers: 'add_members',
    RemoveMembers: 'remove_members',
    AssignRoles: 'assign_roles',
    EditChannel: 'edit_channel',
    EditRoles: 'edit_roles',
    DeleteChannel: 'delete_channel',
    GrantOwnership: 'grant_ownership',
};
export const CHANNEL_PERMISSIONS = Object.values(ChannelPermission);
/** How the settings screens name them, grouped as they are shown. */
export const CHANNEL_PERMISSION_GROUPS = [
    {
        title: 'Okuma ve yazma',
        permissions: [
            { key: 'read', label: 'Kanalı görme ve okuma' },
            { key: 'write', label: 'Mesaj yazma' },
            { key: 'files', label: 'Dosya ve fotoğraf gönderme' },
            { key: 'react', label: 'Tepki ekleme' },
            { key: 'pin', label: 'Mesaj sabitleme' },
            { key: 'mention_everyone', label: '@herkes kullanma' },
            { key: 'delete_others', label: 'Başkalarının mesajını silme' },
        ],
    },
    {
        title: 'Görüşme',
        permissions: [
            { key: 'voice_join', label: 'Görüşmeye katılma (dinleme)' },
            { key: 'voice_speak', label: 'Görüşmede konuşma' },
            { key: 'camera', label: 'Kamera açma' },
            { key: 'screen_share', label: 'Ekran paylaşma' },
            { key: 'voice_moderate', label: 'Görüşmede susturma ve çıkarma' },
        ],
    },
    {
        title: 'Yönetim',
        permissions: [
            { key: 'add_members', label: 'Üye ekleme' },
            { key: 'remove_members', label: 'Üye çıkarma' },
            { key: 'assign_roles', label: 'Rol verme ve alma' },
            { key: 'edit_channel', label: 'Kanalı düzenleme (ad, konu, logo)' },
            { key: 'edit_roles', label: 'Rolleri düzenleme' },
            { key: 'delete_channel', label: 'Kanalı silme' },
            { key: 'grant_ownership', label: 'Sahiplik verme' },
        ],
    },
];
const MEMBER = ['read', 'write', 'files', 'react', 'voice_join', 'voice_speak', 'camera', 'screen_share'];
/**
 * The roles every new channel starts with; their permissions can be changed afterwards. `position`
 * orders them: nobody can give a role above their own, or change someone at or above their level.
 */
export const ROLE_TEMPLATES = [
    { key: 'owner', name: 'Sahip', position: 100, permissions: CHANNEL_PERMISSIONS },
    { key: 'admin', name: 'Yönetici', position: 80, permissions: CHANNEL_PERMISSIONS.filter((p) => p !== 'delete_channel' && p !== 'grant_ownership') },
    { key: 'moderator', name: 'Moderatör', position: 60, permissions: [...MEMBER, 'pin', 'mention_everyone', 'delete_others', 'voice_moderate'] },
    { key: 'member', name: 'Üye', position: 40, permissions: MEMBER },
    { key: 'reader', name: 'Okuyucu', position: 20, permissions: ['read', 'react', 'voice_join'] },
];
const permissionList = z.array(z.enum(CHANNEL_PERMISSIONS)).max(CHANNEL_PERMISSIONS.length);
export const CreateRoleRequest = z.object({
    name: z.string().trim().min(1).max(40),
    permissions: permissionList,
    /** Below the creator's own highest role; defaults to just under Üye's level. */
    position: z.number().int().min(1).max(99).optional(),
});
export const UpdateRoleRequest = z
    .object({ name: z.string().trim().min(1).max(40).optional(), permissions: permissionList.optional(), position: z.number().int().min(1).max(99).optional() })
    .refine((body) => body.name !== undefined || body.permissions !== undefined || body.position !== undefined, { message: 'Nothing to update' });
/** Moves a role one step up or down the list (`POST /channels/:id/roles/:roleId/move`). */
export const MoveRoleRequest = z.object({ direction: z.enum(['up', 'down']) });
/** The roles' order everywhere: highest first; equal ones in the order they were made. */
export const byRank = (a, b) => b.position - a.position || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
/** Replaces someone's roles in a channel. */
export const SetMemberRolesRequest = z.object({ roleIds: z.array(z.uuid()).max(20) });
export const SetDefaultRoleRequest = z.object({ roleId: z.uuid() });
//# sourceMappingURL=roles.js.map